import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import withTableProps from 'HOC/types/withTable';
import { transformTableData, getConfirmationModalConfig } from 'utils';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { actions as modalActions } from 'redux/modal';
import { useAsyncFn } from 'react-use';
import { FormControl, Link, MenuItem, Select, Box, Chip, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { DateFormatter } from 'components/AppTable/CellsComponents';
import { formatTrainingModuleLabel, formatTrainingPartLabel } from './helpers/formatTrainingLabels';

const REQUIRED_SESSION_STATUSES = {
  NOT_ASSIGNED: 'Not Assigned',
  IN_PROGRESS: 'In Progress',
  SCHEDULED: 'Scheduled',
  PASSED: 'Passed',
  FAILED: 'Failed',
  EXPIRED: 'Expired',
};

const useStyles = makeStyles(theme => ({
  formControl: {
    minWidth: 260,
    '& .MuiOutlinedInput-input': {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  sessionLink: {
    cursor: 'pointer',
  },
  scheduledText: {
    color: theme.palette.text.disabled,
  },
  notAssignedText: {
    color: (theme.palette.warning && theme.palette.warning.main) || '#f57c00',
  },
  passedText: {
    color: (theme.palette.success && theme.palette.success.main) || '#2e7d32',
  },
  failedText: {
    color: theme.palette.error.main,
  },
  moduleCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  upcomingChip: {
    height: 22,
    fontWeight: 600,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(144, 202, 249, 0.16)' : '#E3F2FD',
    color: theme.palette.type === 'dark' ? '#90CAF9' : '#1565C0',
  },
}));

const RequiredSessionsTable = ({ tableData, helpers, userId, routesAliases, onSaveStateChange }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const [stagedSelection, setStagedSelection] = useState({});
  const requiredSessionsData = Array.isArray(tableData.data)
    ? tableData.data
    : Array.isArray(tableData.data?.items)
    ? tableData.data.items
    : [];

  const [applyState, applyRequiredSessions] = useAsyncFn(async rows => {
    const response = await helpers.applyRequiredSessions(userId, rows);
    if (!response.error) {
      dispatch(modalActions.closeModal());
      setStagedSelection({});
      tableData.handleForceUpdate();
      dispatch(
        modalActions.openModal(
          { size: 'small', title: t('students.requiredSessionsSummaryTitle') },
          <p>
            {t('students.requiredSessionsSummaryMessage', {
              assigned: response.summary?.assigned || 0,
              failed: response.summary?.failed || 0,
            })}
          </p>,
        ),
      );
    }
    return response;
  });

  const changedRows = useMemo(
    () =>
      Object.keys(stagedSelection)
        .map(trainingPartId => ({
          trainingPartId: Number(trainingPartId),
          trainingSessionId: stagedSelection[trainingPartId] ?? null,
        }))
        .filter(item => item.trainingSessionId !== null),
    [stagedSelection],
  );

  useEffect(() => {
    setStagedSelection(prev => {
      let hasChanges = false;
      const next = { ...prev };
      requiredSessionsData.forEach(row => {
        if (
          Array.isArray(row.candidates) &&
          row.candidates.length === 1 &&
          prev[row.trainingPartId] === undefined
        ) {
          next[row.trainingPartId] = Number(row.candidates[0].id);
          hasChanges = true;
        }
      });
      return hasChanges ? next : prev;
    });
  }, [requiredSessionsData]);

  const handleSave = useCallback(() => {
    if (!changedRows.length) {
      return;
    }
    const [config, element] = getConfirmationModalConfig({
      title: t('students.requiredSessionsConfirmTitle'),
      confirmAction: () => applyRequiredSessions(changedRows),
      rejectAction: () => dispatch(modalActions.closeModal()),
      message: t('students.requiredSessionsConfirmMessage', {
        count: changedRows.length,
      }),
      confirmTitle: t('buttons.save'),
      rejectTitle: t('buttons.cancel'),
    });
    dispatch(modalActions.openModal(config, element));
  }, [applyRequiredSessions, changedRows, dispatch, t]);

  const handleOpenSession = useCallback(
    sessionId => {
      history.push(`${routesAliases.editTrainingSession}/${sessionId}`);
    },
    [history, routesAliases.editTrainingSession],
  );

  const getSessionSelectionComponent = row => {
    if (Array.isArray(row.candidates) && row.candidates.length > 0) {
      const value = stagedSelection[row.trainingPartId] ?? '';
      return (
        <FormControl variant="outlined" size="small" className={classes.formControl}>
          <Select
            value={value}
            displayEmpty
            onChange={event =>
              setStagedSelection(prev => ({
                ...prev,
                [row.trainingPartId]: event.target.value === '' ? null : Number(event.target.value),
              }))
            }
          >
            <MenuItem value="">{t('students.notAssigned')}</MenuItem>
            {row.candidates.map(candidate => (
              <MenuItem key={candidate.id} value={candidate.id}>
                {candidate.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (Array.isArray(row.assignedSessions) && row.assignedSessions.length) {
      return row.assignedSessions.map((session, index) => (
        <React.Fragment key={session.id}>
          {index > 0 ? ', ' : ''}
          <Link className={classes.sessionLink} onClick={() => handleOpenSession(session.id)}>
            {session.displayName}
          </Link>
        </React.Fragment>
      ));
    }

    if (row.status === REQUIRED_SESSION_STATUSES.NOT_ASSIGNED) {
      return t('students.noSessionsAvailableToAssign');
    }

    return t('students.sessionSelectionNotAvailable');
  };

  const getStatusComponent = status => {
    if (status === REQUIRED_SESSION_STATUSES.NOT_ASSIGNED) {
      return <span className={classes.notAssignedText}>{status}</span>;
    }
    if (status === REQUIRED_SESSION_STATUSES.SCHEDULED) {
      return <span className={classes.scheduledText}>{status}</span>;
    }
    if (status === REQUIRED_SESSION_STATUSES.PASSED) {
      return <span className={classes.passedText}>{status}</span>;
    }
    if (status === REQUIRED_SESSION_STATUSES.FAILED || status === REQUIRED_SESSION_STATUSES.EXPIRED) {
      return <span className={classes.failedText}>{status}</span>;
    }
    return status;
  };

  const renderTrainingModuleLabel = (label, isFutureRequirement) => (
    <Box className={classes.moduleCell}>
      <span>{label}</span>
      {isFutureRequirement ? (
        <Tooltip title={t('students.futureRequirementTooltip')} placement="top" arrow>
          <span>
            <Chip
              size="small"
              className={classes.upcomingChip}
              label={t('students.futureRequirementBadge')}
            />
          </span>
        </Tooltip>
      ) : null}
    </Box>
  );

  const tableConfig = [
    {
      title: t('tablesLabels.trainingModule'),
      field: 'trainingModuleLabel',
      dependentField: 'isFutureRequirement',
      Component: renderTrainingModuleLabel,
    },
    {
      title: t('tablesLabels.trainingPart'),
      field: 'trainingPartLabel',
    },
    {
      title: t('tablesLabels.jobsTasks'),
      field: 'roles',
      Component: roles => (Array.isArray(roles) ? roles.join(', ') : ''),
    },
    {
      title: t('complianceStatus'),
      field: 'complianceStatus',
      Component: value =>
        value ? t(`compliance.statuses.${value}`, { defaultValue: value }) : t('compliance.statuses.incomplete'),
    },
    {
      title: t('formLabels.state'),
      field: 'status',
      Component: status => getStatusComponent(status),
    },
    {
      title: t('tablesLabels.expiryDate'),
      field: 'expirationDate',
      Component: value => (value === null ? t('courseDetails.incomplete') : <DateFormatter value={value} />),
    },
    {
      title: t('trainingSessionDetails.session'),
      field: 'sessionSelection',
      Component: row => getSessionSelectionComponent(row),
    },
  ];

  const transformedRows = requiredSessionsData.map(item => ({
    ...item,
    trainingModuleLabel: formatTrainingModuleLabel(item.trainingModuleCode, item.trainingModuleName),
    trainingPartLabel: formatTrainingPartLabel(item.trainingPartVersion, item.trainingMethod),
    sessionSelection: item,
  }));

  const [columns, rows] = transformTableData(tableConfig, transformedRows);
  const isLoading = tableData.isFetchingTableData || applyState.loading;

  useEffect(() => {
    onSaveStateChange({
      hasChanges: changedRows.length > 0,
      isLoading,
      onSave: handleSave,
    });
  }, [changedRows.length, handleSave, isLoading, onSaveStateChange]);

  return (
    <ModalBusyContainer busy={isLoading}>
      <AppTable
        columns={columns}
        data={rows}
        totalCount={tableData.totalCount || 0}
        rowsPerPage={tableData.pageSize || 10}
        isLazyLoading
        onChangePage={tableData.handleChangePage}
      />
    </ModalBusyContainer>
  );
};

RequiredSessionsTable.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  helpers: PropTypes.shape({
    applyRequiredSessions: PropTypes.func.isRequired,
  }).isRequired,
  routesAliases: PropTypes.shape({
    editTrainingSession: PropTypes.string.isRequired,
  }).isRequired,
  onSaveStateChange: PropTypes.func,
};

RequiredSessionsTable.defaultProps = {
  onSaveStateChange: () => {},
};

export default RequiredSessionsTable;
